import type { PluginListenerHandle } from "@capacitor/core";
import type { Piano } from "../piano";
import {
  detectTransport,
  dispatchMidiMessage,
  type MidiActivityListener,
  type MidiDeviceInfo,
  type MidiDeviceTransport,
  type MidiInputAdapter,
  type MidiInputChangeListener,
} from "./midi-shared";
import {
  NativeMidi,
  type NativeMidiDevice,
  type NativeMidiMessageEvent,
  type NativeMidiStateChangeEvent,
} from "./native-midi-plugin";

function asTransport(raw: string): MidiDeviceTransport {
  if (raw === "bluetooth" || raw === "virtual" || raw === "usb") {
    return raw;
  }

  return detectTransport(raw);
}

/**
 * iOS Capacitor implementation of `MidiInputAdapter`. Mirrors the public
 * surface of `WebMidiInput` but talks to the Swift CoreMIDI plugin instead of
 * the (unsupported on iOS) Web MIDI API.
 */
export class NativeMidiInput implements MidiInputAdapter {
  private devices = new Map<string, NativeMidiDevice>();
  private enabledById = new Map<string, boolean>();
  private activeId: string | null = null;

  private changeListeners = new Set<MidiInputChangeListener>();
  private activityListeners = new Set<MidiActivityListener>();

  private messageHandle: PluginListenerHandle | null = null;
  private stateHandle: PluginListenerHandle | null = null;

  constructor(private readonly piano: Piano) {}

  async attach(): Promise<void> {
    this.messageHandle = await NativeMidi.addListener("midiMessage", (e) => this.onMidiMessage(e));
    this.stateHandle = await NativeMidi.addListener("stateChange", (e) => this.onStateChange(e));

    const { devices } = await NativeMidi.listDevices();

    for (const device of devices) {
      this.registerDevice(device);
    }

    this.emitChange();
  }

  detach(): void {
    void this.messageHandle?.remove();
    void this.stateHandle?.remove();
    this.messageHandle = null;
    this.stateHandle = null;

    this.devices.clear();
    this.enabledById.clear();
    this.activeId = null;
    this.piano.allNotesOff();
    this.emitChange();
  }

  rescan(): void {
    void (async () => {
      await NativeMidi.rescan();
      const { devices } = await NativeMidi.listDevices();

      const seen = new Set<string>();

      for (const device of devices) {
        seen.add(device.id);
        this.registerDevice(device);
      }

      for (const id of Array.from(this.devices.keys())) {
        if (!seen.has(id)) {
          this.devices.delete(id);
        }
      }

      this.emitChange();
    })();
  }

  getDevices(): readonly MidiDeviceInfo[] {
    return Array.from(this.devices.values()).map((device) => ({
      id: device.id,
      name: device.name || "Périphérique MIDI",
      manufacturer: device.manufacturer,
      transport: asTransport(device.transport),
      connected: device.connected,
      enabled: this.enabledById.get(device.id) ?? true,
    }));
  }

  getActiveDeviceId(): string | null {
    return this.activeId;
  }

  setActiveDevice(id: string | null): void {
    if (this.activeId === id) {
      return;
    }

    this.activeId = id;
    this.emitChange();
  }

  setEnabled(id: string, enabled: boolean): void {
    const current = this.enabledById.get(id) ?? true;

    if (current === enabled) {
      return;
    }

    this.enabledById.set(id, enabled);
    void NativeMidi.setEnabled({ id, enabled });
    this.emitChange();
  }

  onChange(listener: MidiInputChangeListener): () => void {
    this.changeListeners.add(listener);

    return () => {
      this.changeListeners.delete(listener);
    };
  }

  onActivity(listener: MidiActivityListener): () => void {
    this.activityListeners.add(listener);

    return () => {
      this.activityListeners.delete(listener);
    };
  }

  /** iOS-only helper to open the BLE MIDI pairing sheet. */
  presentBluetoothPairingUI(): Promise<void> {
    return NativeMidi.presentBluetoothPairingUI();
  }

  private registerDevice(device: NativeMidiDevice): void {
    this.devices.set(device.id, device);

    if (!this.enabledById.has(device.id)) {
      this.enabledById.set(device.id, true);
      void NativeMidi.setEnabled({ id: device.id, enabled: true });
    }
  }

  private onStateChange(event: NativeMidiStateChangeEvent): void {
    if (event.state === "disconnected") {
      this.devices.delete(event.id);
      this.emitChange();

      return;
    }

    void (async () => {
      const { devices } = await NativeMidi.listDevices();
      const fresh = devices.find((d) => d.id === event.id);

      if (fresh) {
        this.registerDevice(fresh);
        this.emitChange();
      }
    })();
  }

  private onMidiMessage(event: NativeMidiMessageEvent): void {
    if (this.enabledById.get(event.id) === false) {
      return;
    }

    const result = dispatchMidiMessage(this.piano, event.data);

    if (result?.kind === "noteOn") {
      this.emitActivity({ id: event.id, midi: result.midi, velocity: result.velocity });
    }
  }

  private emitChange(): void {
    for (const listener of this.changeListeners) {
      listener();
    }
  }

  private emitActivity(payload: { id: string; midi: number; velocity: number }): void {
    for (const listener of this.activityListeners) {
      listener(payload);
    }
  }
}
